import { Data } from 'billboard.js/types/options';
import { namespaces, select } from 'd3';
import { isArray, isObject } from '@datorama/akita';
import {
    area,
    areaLineRange,
    areaSpline,
    areaSplineRange,
    bar,
    bubble,
    Chart,
    ChartOptions,
    donut,
    line,
    spline
} from 'billboard.js';
import {
    ChartClasses,
    ChartDataGroup,
    ExtendedChartOptions,
    ExtendedChartTypes,
    ExtendedChartTypesMapping,
    ExtendedData
} from './bb-chart.interfaces';
import Bubblecompare from 'billboard.js/dist/plugin/billboardjs-plugin-bubblecompare';
import { formatNumber } from '@angular/common';
import numeral from 'numeral';
import { Axis } from 'billboard.js/types/axis';
import { get } from 'dot-prop';
import { floor } from 'mathjs';

// Todo: move to general utilities?
export const fromCamelCaseToSentence = (camelCase) =>
    (camelCase.charAt(0).toUpperCase() + camelCase.slice(1))
        .replace(/([A-Z][a-z]+)/g, ' $1')
        .replace(/([A-Z]{2,})/g, ' $1')
        .replace(/\s{2,}/g, ' ')
        .trim();

// Todo: move to general utilities?
export const toCamelCase = (string) =>
    string.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());

/** Add gradient
 * Adds gradients to svg definition that can be used by billboard for svg fills.
 * @param {ExtendedChartTypes | { [key: string]: ExtendedChartTypes }} chartType - Chart type(s) to generate gradients for.
 * @returns {Array<SVGPathElement>} - Array with svg path elements.
 */
export const addGradient = (
    chartType: ExtendedChartTypes | { [key: string]: ExtendedChartTypes }
): Array<SVGPathElement> =>
    (!isObject(chartType) ? [<ExtendedChartTypes>chartType] : Object.values(chartType))
        // only add gradients for the following chart types...
        .filter(
            (type) =>
                [
                    'area',
                    'area-line-range',
                    'area-spline',
                    'area-spline-range',
                    'area-step'
                ].indexOf(type) !== -1
        )
        .reduce((previousValue, currentValue, i) => {
            const gradient = select(
                document.createElementNS(namespaces.svg, 'linearGradient')
            )
                .attr('x1', '0')
                .attr('data-id', i)
                .attr('x2', '0')
                .attr('y1', '0')
                .attr('y2', '1');
            gradient.append('stop').attr('offset', '0');
            gradient.append('stop').attr('offset', '1');
            return [<SVGPathElement>gradient.node(), ...previousValue];
        }, []);

/** Generate class names
 * These class names are added to each data point by billboard and are used to style data points using css.
 * @param {Data} data - Data to generate class names for.
 * @returns {{ [key: string]: string }} - Object with key value pairs were key equals name of series and value equals class name to use.
 */
export const generateClassNames = (data: ExtendedData): { [key: string]: string } => {
    if (data?.json && isArray(data.json) && data.json.length > 0) {
        return <{ [key: string]: string }>data.json.reduce(
            (previous: any, current, index) => {
                const group = Object.keys(current).filter(
                    (key) =>
                        key !== 'name' && // key different from name
                        !/^x\d{0,2}$/.test(key) && // key different from x, x0, x1, x10 etc.
                        key !== '_id' // key different from _id
                );
                return {
                    ...previous,
                    ...group.reduce((previousValue, currentValue, i) => {
                        const length = group.length;
                        const stepSize = floor(10 / (length - 1));
                        const y = i === 0 ? 1 : i * stepSize;
                        return {
                            ...previousValue,
                            [currentValue]: `group-color-${index + 1} value-color-${y}${
                                (currentValue + '').indexOf('benchmark') !== -1
                                    ? ` benchmark-color benchmark-color-${index + 1}`
                                    : ''
                            }`
                        };
                    }, {})
                };
            },
            {}
        );
    } else if (data?.columns) {
        return data.columns
            .filter(
                (column) => !/^x\d{0,2}$/.test(column[0] + '') // key different from x, x0, x1, x10 etc.
            )
            .reduce((previousValue, currentValue, index) => {
                const isBenchmark = (currentValue + '').indexOf('benchmark') !== -1;
                let benchmarkIndex = 0;
                if (isBenchmark) {
                    benchmarkIndex = Object.keys(previousValue).filter(
                        (key) => key.indexOf('benchmark') !== -1
                    ).length;
                }
                return {
                    ...previousValue,
                    [currentValue[0] + '']: `group-color-${index + 1} value-color-1${
                        isBenchmark
                            ? ` benchmark-color benchmark-color-${benchmarkIndex + 1}`
                            : ''
                    }`
                };
            }, {});
    } else if (data?.rows) {
        return data.rows[0]
            .filter((key) => key !== 'x')
            .reduce((previousValue: any, currentValue, index) => {
                const isBenchmark = (currentValue + '').indexOf('benchmark') !== -1;
                let benchmarkIndex = 0;
                if (isBenchmark) {
                    benchmarkIndex = Object.keys(previousValue).filter(
                        (key) => key.indexOf('benchmark') !== -1
                    ).length;
                }
                return {
                    ...previousValue,
                    [currentValue + '']: `group-color-${index + 1} value-color-1${
                        isBenchmark
                            ? ` benchmark-color benchmark-color-${benchmarkIndex + 1}`
                            : ''
                    }`
                };
            }, {});
    }
};
/** Group data
 * @param {ExtendedData} data - Data to be grouped.
 * @returns {Array<string>} - Array with name of series.
 */
export const groupData = (data: ExtendedData): Array<string> => {
    if (data?.json && isArray(data.json) && data.json.length > 0) {
        return [
            (<Array<{ [key: string]: string }>>data.json).reduce(
                (previous: any, current) => [
                    ...previous,
                    ...Object.keys(current).filter((key) => key !== 'name')
                ],
                []
            )
        ];
    } else {
        return [];
    }
};
/** Tooltip template
 * @param {Array<any>} data - Data for tooltip.
 * @param {ChartClasses} classes - Classes used by chart for series.
 * @param {ExtendedChartTypesMapping} [mappings] - Optional mappings for describing 3 dimensional data (bubble compare charts).
 * @returns {string} - html template string.
 */
export const tooltipTmpl = (
    data: Array<any>,
    classes: ChartClasses,
    mappings?: ExtendedChartTypesMapping
): string => `<dl class="bb-tooltip">
                              ${data
                                  .filter((item) => item?.value !== null)
                                  .map((item) => {
                                      const negativeClass =
                                          classes[item?.id] &&
                                          classes[item?.id].indexOf(
                                              'bb-target-negative-value'
                                          ) === -1
                                              ? ''
                                              : 'bb-target-negative-value';

                                      const id = item?.id;
                                      let classNames =
                                          classes[id] &&
                                          classes[id].indexOf(
                                              'bb-target-negative-value'
                                          ) === -1
                                              ? ''
                                              : 'bb-target-negative-value';
                                      if (
                                          classes[id] &&
                                          classes[id].indexOf('benchmark') !== -1
                                      ) {
                                          classNames = classes[id];
                                      }
                                      const color =
                                          Object.keys(classes).length > 0
                                              ? classes[item?.id] &&
                                                item.id &&
                                                classes[item?.id].indexOf('benchmark') !==
                                                    -1
                                                  ? 'var(--chart-benchmark)'
                                                  : (classes[item?.id] || '')
                                                        .replace(
                                                            / bb-target-negative-value/,
                                                            ''
                                                        )
                                                        .replace(
                                                            /group-color-/,
                                                            'var(--bb-chart-serie-'
                                                        )
                                                        .replace(
                                                            / value-color-/,
                                                            '-color-'
                                                        ) + ')'
                                              : 'color';
                                      // if item value contains z value and mappings are provided (i.e. we're working with 3 dimensional data)...
                                      return mappings
                                          ? `<div class="column" style="--color: ${color}">
                                      <dt class="${classNames}">${fromCamelCaseToSentence(
                                                item?.name
                                            )}</dt>
                                      <dd><span>${fromCamelCaseToSentence(
                                          mappings.y
                                      )}</span>${formatNumber(item?.value.y, 'sv')}</dd>
                                      <dd><span>${fromCamelCaseToSentence(
                                          mappings.z
                                      )}</span>${formatNumber(item?.value.z, 'sv')}</dd>
                                      <dd><span>${fromCamelCaseToSentence(
                                          mappings.x
                                      )}</span>${formatNumber(item?.x, 'sv')}</dd>
                                    </div>` // ...else return normal template
                                          : `<div style="--color: ${color}">
                                      <dt class="${classNames}">${fromCamelCaseToSentence(
                                                item?.name
                                            )}</dt>
                                      <dd>${
                                          negativeClass !== '' ? '-' : ''
                                      }${formatNumber(item?.value, 'sv')}${
                                                item?.ratio
                                                    ? `<span>(${(
                                                          item?.ratio * 100
                                                      ).toFixed(2)}%)</span>`
                                                    : ''
                                            }</dd>
                                    </div>`;
                                  })
                                  .join('')}
                            </dl>`;

/** Legend template
 * @param {string} id - Id for legend.
 * @param {Chart} chart - Chart reference.
 * @param percentageOptions - Legend percentage settings
 * @returns {string} - html template string.
 */
export const legendTmpl = (
    id: string,
    chart: Chart,
    percentageOptions: { show: boolean; locale: string }
): string => {
    const classes = chart.config('data.classes');
    let classNames =
        classes[id] && classes[id].indexOf('bb-target-negative-value') === -1
            ? ''
            : 'bb-target-negative-value';
    if (classes[id] && classes[id].indexOf('benchmark') !== -1) {
        classNames = classes[id];
    }
    const color =
        classes && id && classes[id]
            ? //classes[id].indexOf('benchmark') !== -1
              //? 'var(--chart-benchmark)' :
              classes[id]
                  .replace(/group-color-/, 'var(--bb-chart-serie-')
                  .replace(/ bb-target-negative-value/, '')
                  .replace(/ value-color-/, '-color-')
                  .replace(/ benchmark-color/g, '') + ')'
            : 'color';

    let _benchmarkPct = '';
    if (percentageOptions.show) {
        const _benchmarkData = chart.data.values(id);
        const formatter = new Intl.NumberFormat(percentageOptions.locale || 'en-US', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        _benchmarkPct = `${formatter.format(
            Number(_benchmarkData[_benchmarkData.length - 1])
        )}`;
    }

    const defaultContent = `${fromCamelCaseToSentence(
        id.replace(/_benchmark_/g, '').replace(/event/g, '')
    )}`;
    return `<dt style="--color: ${color}; --default-content: '${defaultContent} ${_benchmarkPct}'" class="${classNames}"></dt>`;
};

/** Load type
 * @param {ExtendedChartTypes} type - Chart type to load default options for.
 * @param {ExtendedChartOptions} options - Options to extend/modify.
 * @param {ExtendedData} data - Data used by chart.
 * @returns {Partial<ExtendedChartOptions>} - Modified chart options.
 */
export const loadType = (
    type: ExtendedChartTypes,
    options: ExtendedChartOptions,
    data: ExtendedData
): Partial<ChartOptions> => {
    if (type === 'area') {
        area();
        return {
            data: {
                ...(<Data>data)
            },
            color: {
                tiles: () => [...addGradient(options.data.type || options.data.types)]
            }
        };
    } else if (type === 'bar') {
        bar();
        return {
            data: {
                ...(<Data>data)
            }
        };
    } else if (type === 'bar-category') {
        bar();
        return {
            data: {
                ...(<Data>data),
                labels: {
                    centered: true,
                    format: (value, id, index, j) => {
                        return id
                            ? options.data?.names
                                ? options.data?.names[id]
                                : fromCamelCaseToSentence(id)
                            : '';
                    }
                },
                type: 'bar'
            },
            axis: {
                x: {
                    type: 'category'
                }
            }
        };
    } else if (type === 'bar-stack-horizontal') {
        bar();
        const groups: any = groupData(data);

        return {
            data: {
                ...(<Data>data),
                groups,
                order: (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
                keys: {
                    x: 'name',
                    value: groups[0]
                },
                labels:
                    options.data.labels != null
                        ? options.data.labels
                        : {
                              centered: true,
                              format: (value, id, index, j) => {
                                  return (
                                      options.data.names[id] ||
                                      fromCamelCaseToSentence(id)
                                  );
                              }
                          },
                type: 'bar'
            },
            axis: {
                x: {
                    type: 'category'
                },
                rotated: true
            }
        };
    } else if (type === 'area-spline') {
        areaSpline();
        return {
            data: {
                ...(<Data>data)
            },
            color: {
                tiles: () => [...addGradient(options.data.type || options.data.types)]
            }
        };
    } else if (type === 'line') {
        line();
        return {};
    } else if (type === 'area-line-range') {
        areaLineRange();
        return {
            data: {
                ...(<Data>data)
            },
            color: {
                //tiles: () => [...addGradient(options.data.type || options.data.types)]
            }
        };
    } else if (type === 'area-spline-range') {
        areaSplineRange();
        return {
            data: {
                ...(<Data>data)
            },
            color: {
                //tiles: () => [...addGradient(options.data.type || options.data.types)]
            }
        };
    } else if (type === 'spline') {
        spline();
        return {
            data: {
                ...(<Data>data)
            }
        };
    } else if (type === 'bubble') {
        bubble();
        return {};
    } else if (type === 'bubble-compare') {
        // transform data
        bubble();
        return {
            data: {
                ...(<any>data),
                type: 'bubble',
                labels: {
                    format: (x) => numeral(x).format('0.00a')
                }
            },
            axis: {
                ...options.axis,
                x: {
                    ...options?.axis?.x,
                    tick: {
                        fit: true,
                        ...options?.axis?.x?.tick
                    }
                }
            },
            plugins: [
                new Bubblecompare({
                    minR: 10,
                    maxR: 80,
                    expandScale: 1.1
                })
            ]
        };
    } else if (type === 'donut') {
        const adjustedClasses = { ...(data.classes || generateClassNames(data)) };
        if (data.json) {
            const absoluteValues = {};
            Object.entries<number>(data?.json[0] || {}).forEach(([key, value], index) => {
                if (value < 0) {
                    adjustedClasses[key] += ' bb-target-negative-value';
                }
                absoluteValues[key] = Math.abs(value);
            });
            data = { ...data, json: [absoluteValues], classes: adjustedClasses };
        }
        if (data.columns) {
            const absoluteValues = [];
            (data.columns as Array<[string, number]>).forEach(([key, value], index) => {
                if (value < 0) {
                    adjustedClasses[key] += ' bb-target-negative-value';
                }
                absoluteValues[index] = [key, Math.abs(value)];
            });
            data = { ...data, columns: absoluteValues, classes: adjustedClasses };
        }
        donut();
        return {
            data: {
                ...(<Data>data)
            }
        };
    } else if (type === 'benchmark-line') {
        spline();
        return {
            data: {
                ...(<Data>options.data),
                types: {
                    ...options.data.types,
                    benchmark: 'spline'
                }
            }
        };
    }
    return {};
};

/** Bubble data - transform json data into 3 dimensional json structure for use i bubble compare chart.
 * @param {Array<{[key: string]: string | number}>} jsonData - Chart data in json format (array with key value pairs).
 * @param {string} x - Data key to use for x axis.
 * @param {string} y - Data key to use for y axis.
 * @param {string} z - Data key to use for z (size of bubble).
 * @param {string} name - Data key to use as name for data.
 * @param {string} [id] - Optional id for data point, if omitted one will be created using name.
 * @returns {BubbleCompareDataOutput} - Modified data for bubble compare chart.
 */
const bubbleData = (
    jsonData: BubbleCompareDataInput,
    x: string,
    y: string,
    z: string,
    name: string,
    id?: string
): BubbleCompareDataOutput => {
    const json: Array<BubbleCompareData> = jsonData?.map((item) => {
        const _id: string = toCamelCase(item[id || name]);
        return <BubbleCompareData>{
            name: item[name] + '',
            x: (<number>item[x]).toFixed(1),
            _id,
            [_id]: { y: <number>item[y], z: <number>item[z] }
        };
    });
    return {
        json,
        keys: {
            x: 'x',
            value: json.map((item) => item._id)
        }
    };
};

export type BubbleCompareDataInput = Array<{
    [key: string]: string | number;
}>;

interface BubbleCompareDataOutput {
    json: Array<BubbleCompareData>;
    keys: { x: 'x'; value: Array<string> };
}

interface BubbleCompareDataPoint {
    y: number;
    z: number;
}

type BubbleCompareData = {
    name: string;
    x: string;
    _id: string;
} & {
    [key: string]: BubbleCompareDataPoint;
};

export const mapJsonToColumns = (
    jsonArray: Array<{ [key: string]: any }>,
    nameKey: string,
    valueKey: string,
    defaultValue?: number | null
) => {
    const COLUMNS = [];
    for (let i = 0; i < jsonArray.length; i++) {
        const ROW_OBJECT = jsonArray[i];
        const NAME = get(ROW_OBJECT, nameKey);
        const VALUE = get(ROW_OBJECT, valueKey, defaultValue);
        COLUMNS[i] = [NAME, VALUE];
    }
    return {
        columns: COLUMNS
    };
};

/** mapJsonToBubbleCompare - map json array to columns for bubble compare chart
 @param {Array<{ [key: string]: any }>} jsonArray - JSON array with chart data
 @param {ExtendedChartTypesMapping} mappings - Keys to be mapped to chart data points.
 @returns { {
            data: ExtendedData;
            axis: Axis;
        } } - Data options for bubble chart.
 */
export const mapJsonToBubbleCompare = (
    jsonArray: Array<{ [key: string]: any }>,
    mappings: ExtendedChartTypesMapping
): {
    data: ExtendedData;
    axis: Axis;
} => <
        {
            data: ExtendedData;
            axis: Axis;
        }
    >(<
        Array<{
            data: ExtendedData;
            axis: Axis;
        }>
    >[
        jsonArray.reduce(
            (previous, current, index) => ({
                mappings,
                xs: { ...previous?.xs, [current[mappings.name]]: `x${index}` },
                columns: [
                    ...previous.columns,
                    [`x${index}`, current[mappings.x]],
                    [
                        current[mappings.name],
                        { y: current[mappings.y], z: current[mappings.z] }
                    ]
                ]
            }),
            { xs: {}, columns: [] }
        )
    ].map((data) => ({
        data,
        axis: {
            x: {
                padding: {
                    left: Math.ceil(
                        Math.min(...jsonArray.map((item) => item[mappings.x])) * 0.5
                    ),
                    right: Math.ceil(
                        Math.max(...jsonArray.map((item) => item[mappings.x])) * 0.1
                    )
                }
            },
            y: {
                padding: {
                    bottom: Math.ceil(
                        Math.max(...jsonArray.map((item) => item[mappings.y])) * 100
                    ),
                    top: Math.ceil(
                        Math.max(...jsonArray.map((item) => item[mappings.y])) * 120
                    )
                }
            }
        }
    })))[0];

/** prepareGroups - group data for chart with `type: 'bar-stack-horizontal'`
 @param {Array<ChartDataGroup>} data - data to be grouped
 @returns { {
            json: Object;
        } } - Grouped data for bar-stack-horizontal.
 */
export const prepareGroups = (data: Array<ChartDataGroup>): Data => {
    const json = Object.values(
        data.reduce((previousValue, currentValue) => {
            const name = fromCamelCaseToSentence(currentValue.group);
            const key = currentValue.name;
            const value = currentValue.value;
            let group = previousValue[name] || { name };
            group = { ...group, [key]: value };
            return { ...previousValue, [name]: group };
        }, {})
    );
    return { json };
};
